/**
 * Настройки оператора: разбор, миграция, сериализация.
 *
 * Первые настоящие пользовательские настройки в приложении. До вехи M5
 * localStorage трогал ровно один модуль — api/token.ts, — и трогал он не
 * настройку, а секрет.
 *
 * Модуль ЧИСТЫЙ: никакого localStorage. Ввод-вывод живёт в store/useSettings.ts.
 * Разделение не косметическое — оно позволяет проверить таблицу порчи данных
 * обычным node-тестом, а не через jsdom.
 *
 * Почему не zustand/persist. Дело не в размере зависимости (её нет), а в том,
 * что persist ПРЯЧЕТ отказ: ошибка разбора превращается в тихий сброс к
 * умолчаниям, а тихий сброс списка размеров — это ордер неверного размера.
 * Здесь каждый такой случай возвращает notice, и его видно на экране.
 */

import { decodeTarget, type TargetChoice } from "./accounts";
import { parse } from "./money";
import { DEFAULT_SIZES, normalizeSizes } from "./presets";

/** Версия схемы. Растёт, когда меняется смысл полей, а не их набор. */
export const SETTINGS_VERSION = 1;

export interface Settings {
  v: number;
  /** Пресеты размера. Канонические строки, отсортированы, без дублей. */
  sizes: string[];
  /** Индекс выбранного пресета. Зажимается в границы списка. */
  activeSize: number;
  /**
   * Засеяны ли настройки списком трейдера.
   *
   * Нужен, чтобы отличить «оператор согласился с трейдером» от «оператор ещё ни
   * разу не видел настроек»: первый набор берётся у трейдера, дальше правки
   * оператора уважаются и молча не перезаписываются.
   *
   * «Боевого режима» здесь нет НАМЕРЕННО: он не переживает перезагрузку.
   * Страница, которую только что открыли, — это страница, на которой оператор
   * ещё не сориентировался.
   */
  seeded: boolean;
  /**
   * Ручная сумма, КАК ЕЁ НАБРАЛ оператор. "" — поле пустое.
   *
   * Сырой текст, а не каноническая форма: канонизация на каждое нажатие съедала
   * бы точку в «1.» и дралась бы с набором. Канонизируется в точке
   * использования — тем же приёмом, каким OrderForm держит сырой usd, а
   * SizePresetsEditor — сырой текст с предпросмотром.
   */
  customSize: string;
  /**
   * Что выбрано сейчас: пресет или ручная сумма.
   *
   * Отдельным полем, а не `activeSize: number | "custom"`. Объединение
   * заставило бы ветвиться каждого потребителя и, главное, теряло бы набранную
   * сумму при клике по пресету — оператор ввёл её один раз и не должен вводить
   * снова, чтобы к ней вернуться.
   */
  sizeMode: SizeMode;
  /**
   * Развёрнута ли форма ордера на странице рынка.
   *
   * Умолчание — развёрнута: форма это ТОЧНЫЙ путь отправки (своя цена, GTD, FOK,
   * FAK), и прятать его у оператора, который ещё не выбирал, нельзя.
   *
   * Сохраняется, в отличие от «свёрнутых стаканов» и боевого режима, и разница
   * содержательная: те два состояния опасно наследовать из прошлого сеанса —
   * взведённые клавиши тратят деньги, а скрытый стакан прячет цену. Свёрнутая
   * форма не прячет ничего и не делает ничего: это ответ на вопрос «работаю ли я
   * клавишами или мышью», и оператор отвечает на него один раз, а не при каждом
   * открытии вкладки.
   */
  orderFormOpen: boolean;
  /**
   * Режим СИМУЛЯЦИИ: форма, быстрые кнопки и хоткеи шлют пробные ордера.
   *
   * Сохраняется — в отличие от боевого режима, и это не противоречие, а его
   * зеркало. Опасно наследовать из прошлого сеанса состояние, при котором
   * нажатие ТРАТИТ деньги; здесь наоборот — наследуется состояние, при котором
   * оно их НЕ тратит.
   *
   * Обратная ошибка («думал, что поставил настоящий ордер, а это была проба»)
   * закрывается показом: пока режим включён, полоса рынка перекрашена и о нём
   * говорит баннер, который нельзя прокрутить с экрана. У выключенного режима
   * такого признака нет и быть не может — это обычное состояние терминала, —
   * поэтому именно включённый и обязан быть тем, который виден постоянно.
   */
  paperMode: boolean;
  /**
   * АКТИВНАЯ СТРОКА быстрых действий: от её имени бьют горячие клавиши, берутся
   * сигналы, считается тир и закрывается пробная позиция. null — не выбрана.
   *
   * Запоминается, но в запросе едет ЯВНО: умолчания «единственный аккаунт»
   * нет (инвариант 4 roadmap-accounts). Выбор, выпавший из состава, не
   * стирается — панель просто его не применяет и просит выбрать заново.
   */
  target: TargetChoice | null;
  /**
   * Цель формы «Новый ордер» — своя, отдельно от активной строки (замечание
   * владельца 2026-10-07: выбор у формы действует только на неё). Кнопки
   * быстрых действий цель берут из своей строки, а не отсюда.
   */
  formTarget: TargetChoice | null;
  /**
   * Площадка формы ордера на странице рынка (ADR-0040). predict — проба:
   * один кошелёк, без аккаунтов и когорт, только лимитные ордера.
   */
  venue: Venue;
}

export type Venue = "polymarket" | "predict";

// Режим порога замка ЗДЕСЬ БОЛЬШЕ НЕ ЖИВЁТ. Он уехал на сервер ключом lock:mode,
// и это не перенос ради переноса: тем же режимом судит подсистема алертов в
// трейдере, поднимая сквозную карточку со сделкой. Настройка в localStorage
// одного браузера означала бы, что панель говорит об окне одно, а карточка —
// другое, причём тихо. Тип режима теперь в api/types.ts, рядом с остальным
// контрактом провода.

export type SizeMode = "preset" | "custom";

export interface Notice {
  level: "warn" | "info";
  message: string;
}

export interface Decoded {
  settings: Settings;
  notices: Notice[];
}

export function defaultSettings(): Settings {
  return {
    v: SETTINGS_VERSION,
    sizes: [...DEFAULT_SIZES],
    activeSize: 0,
    seeded: false,
    customSize: "",
    sizeMode: "preset",
    orderFormOpen: true,
    paperMode: false,
    target: null,
    formTarget: null,
    venue: "polymarket",
  };
}

/**
 * Разбор сохранённого значения.
 *
 * Правила, каждое из которых — случай в таблице теста:
 *
 *   - ключа нет            → умолчания БЕЗ notice (первый запуск не ошибка);
 *   - нечитаемый JSON      → умолчания + notice, ключ НЕ удаляется (испорченное
 *                            значение может пригодиться при разборе инцидента);
 *   - версия больше нашей  → умолчания + notice; вниз не мигрируем;
 *   - версия меньше нашей  → migrate();
 *   - часть размеров битая → остальные сохраняются, выброшенные названы;
 *   - размеров не осталось → умолчания + notice (ноль пресетов делает каждый
 *                            хоткей молчаливым no-op);
 *   - activeSize вне границ → зажимается;
 *   - customSize не строка  → "", МОЛЧА (дополнительное поле);
 *   - sizeMode не из набора → "preset", МОЛЧА;
 *   - sizeMode = custom при негодной сумме → "preset" + notice: иначе вкладка
 *                            открылась бы с мёртвыми кнопками покупки.
 */
export function decodeSettings(raw: string | null | undefined): Decoded {
  if (raw === null || raw === undefined || raw === "") {
    return { settings: defaultSettings(), notices: [] };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {
      settings: defaultSettings(),
      notices: [
        {
          level: "warn",
          message: "настройки не прочитаны и заменены умолчаниями — проверьте размеры",
        },
      ],
    };
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return {
      settings: defaultSettings(),
      notices: [
        { level: "warn", message: "настройки имеют неожиданный вид и заменены умолчаниями" },
      ],
    };
  }

  const bag = parsed as Record<string, unknown>;
  const notices: Notice[] = [];

  const version = typeof bag["v"] === "number" ? bag["v"] : 0;
  if (version > SETTINGS_VERSION) {
    return {
      settings: defaultSettings(),
      notices: [
        {
          level: "warn",
          message:
            `настройки версии ${version} новее, чем понимает эта вкладка ` +
            `(${SETTINGS_VERSION}) — включены умолчания`,
        },
      ],
    };
  }
  if (version < SETTINGS_VERSION) {
    notices.push({
      level: "info",
      message: `настройки версии ${version} обновлены до ${SETTINGS_VERSION}`,
    });
  }

  const rawSizes = Array.isArray(bag["sizes"])
    ? (bag["sizes"] as unknown[]).map((s) => String(s))
    : [];
  const normalized = normalizeSizes(rawSizes);
  for (const n of normalized.notices) {
    notices.push({ level: "warn", message: n.message });
  }

  let sizes = normalized.sizes;
  if (sizes.length === 0) {
    sizes = [...DEFAULT_SIZES];
    notices.push({
      level: "warn",
      message: "в настройках не осталось ни одного размера — включены умолчания",
    });
  }

  let activeSize = typeof bag["activeSize"] === "number" ? Math.trunc(bag["activeSize"]) : 0;
  if (activeSize < 0 || activeSize >= sizes.length) {
    if (typeof bag["activeSize"] === "number") {
      notices.push({ level: "info", message: "выбранный размер вышел за границы списка" });
    }
    activeSize = 0;
  }

  // Отсутствующее ДОПОЛНИТЕЛЬНОЕ поле заполняется молча: совместимость вперёд
  // не должна пугать оператора замечаниями.
  const seeded = bag["seeded"] === true;
  const customSize = typeof bag["customSize"] === "string" ? bag["customSize"] : "";

  // Режим восстанавливается только вместе с ГОДНОЙ суммой. Вкладка, открывшаяся
  // с выбранной ручной суммой, которую нечем разобрать, — это вкладка с
  // мёртвыми кнопками покупки, и причина была бы неочевидна. Замечание
  // информационное, а не предупреждение: деньги не потеряны, потерян выбор.
  let sizeMode: SizeMode = bag["sizeMode"] === "custom" ? "custom" : "preset";
  if (sizeMode === "custom") {
    const parsed = parse(customSize);
    if (parsed === null || parsed <= 0) {
      sizeMode = "preset";
      notices.push({
        level: "info",
        message: `сохранённая ручная сумма «${customSize}» не разобрана — выбран пресет`,
      });
    }
  }

  // Свёрнутость формы — тоже ДОПОЛНИТЕЛЬНОЕ поле, и умолчание у него
  // «развёрнута»: набор без этого ключа оставлен оператором, который формой
  // пользовался, и открывать ему вкладку со спрятанным точным путём не за что.
  // Версия схемы из-за поля не растёт — меняется набор полей, а не смысл.
  const orderFormOpen = bag["orderFormOpen"] !== false;

  // Режим симуляции — ДОПОЛНИТЕЛЬНОЕ поле с умолчанием «выключен»: набор без
  // этого ключа оставлен до появления проб, и открывать по нему вкладку в
  // режиме, о котором оператор не просил, нельзя. Строгое сравнение с true, а
  // не приведение: любое неожиданное значение обязано означать боевой режим —
  // то есть тот, в котором терминал работал всегда.
  const paperMode = bag["paperMode"] === true;

  // Цель команд — ДОПОЛНИТЕЛЬНОЕ поле: набор без него оставлен до аккаунтов,
  // и выбора у оператора тогда не было. Непонятное значение — «не выбрано»,
  // без notice: оператор увидит это сам по подсказке «выберите аккаунт», а
  // угаданный выбор был бы ордером не тем аккаунтом.
  const target = decodeTarget(bag["target"]);
  // Цель формы — то же правило. Набор до разделения её не знал, и форма
  // начинает без выбора: унаследовать активную строку значило бы молча отдать
  // форме цель, которую оператор выбирал для другого.
  const formTarget = decodeTarget(bag["formTarget"]);
  // Площадка — ДОПОЛНИТЕЛЬНОЕ поле. Всё, кроме точного "predict", — Polymarket:
  // форма, открывшаяся не на той площадке, — это ордер не туда.
  const venue: Venue = bag["venue"] === "predict" ? "predict" : "polymarket";

  // Ключ lockMode в старых наборах ИГНОРИРУЕТСЯ молча, и версия схемы из-за
  // этого не растёт: поле уехало на сервер, а лишний ключ в localStorage ничему
  // не мешает — читатель его больше не спрашивает.
  return {
    settings: {
      v: SETTINGS_VERSION,
      sizes,
      activeSize,
      seeded,
      customSize,
      sizeMode,
      orderFormOpen,
      paperMode,
      target,
      formTarget,
      venue,
    },
    notices,
  };
}

export function encodeSettings(s: Settings): string {
  return JSON.stringify({
    v: SETTINGS_VERSION,
    sizes: s.sizes,
    activeSize: s.activeSize,
    seeded: s.seeded,
    customSize: s.customSize,
    sizeMode: s.sizeMode,
    orderFormOpen: s.orderFormOpen,
    paperMode: s.paperMode,
    target: s.target,
    formTarget: s.formTarget,
    venue: s.venue,
  });
}
